/**
 * ARIA Voice-Intent Architecture
 * ---------------------------------------------------------------------------
 * Extends the classic ARIA_COMMANDS nav dispatcher with chairside "verb
 * intents" that map to actions (not just paths). Additive — none of the
 * existing nav vocabulary changes.
 *
 * Live intents (wired now):
 *   • open_ask_the_bench      → /quick-assist
 *   • open_case_intake        → /quick-assessment/new
 *   • open_my_last_case       → resume last chairside case (localStorage hint)
 *   • take_me_back            → window.history.back()
 *   • repeat_that             → replays the last ARIA spoken line
 *
 * Registered-but-stubbed (recognised, no-ops for now — architecture ready):
 *   • show_todays_cases       → future filter on Recent Cases
 *   • search_for_case         → future one-tap search on Recent Cases
 *   • start_a_new_case        → same slot as open_case_intake; kept distinct
 *                                for analytics (voice user chose the "new"
 *                                phrasing over the noun)
 *   • summarize_this_case     → future in-context summarizer
 *
 * Contract: matchVoiceIntent(text) returns the highest-priority match
 * or null. Priority is dictionary order. A matched intent has:
 *   { id, label, kind: "live" | "stub", action?, path? }
 */

// Registered intents. Order matters — first regex match wins.
export const VOICE_INTENTS = [
  {
    id: "open_ask_the_bench",
    label: "Ask the Bench",
    kind: "live",
    path: "/quick-assist",
    patterns: [
      /^(?:open|launch|show|start)\s+ask\s+the\s+bench\b/i,
      /^ask\s+the\s+bench\b/i,
    ],
  },
  {
    id: "start_a_new_case",
    label: "New case intake",
    kind: "live",
    path: "/quick-assessment/new",
    patterns: [
      /^start\s+(?:a\s+)?new\s+case(?:\s+intake)?\b/i,
      /^new\s+case\s+intake\b/i,
      /^begin\s+(?:a\s+)?new\s+case\b/i,
    ],
  },
  {
    id: "open_case_intake",
    label: "Case Intake",
    kind: "live",
    path: "/quick-assessment/new",
    patterns: [
      /^(?:open|show|launch)\s+case\s+intake\b/i,
      /^case\s+intake\b/i,
    ],
  },
  {
    id: "open_my_last_case",
    label: "Resume last case",
    kind: "live",
    // No path — the caller looks up the last case ID from chairsideStore
    // and navigates dynamically. Setting `action: "resume_last_case"`
    // signals this to the runCommand handler.
    action: "resume_last_case",
    patterns: [
      /^(?:open|resume|show)\s+(?:my\s+)?last\s+case\b/i,
      /^(?:take\s+me\s+to\s+|go\s+to\s+)?(?:my\s+)?last\s+case\b/i,
    ],
  },
  {
    id: "take_me_back",
    label: "Back",
    kind: "live",
    action: "history_back",
    patterns: [
      /^(?:take\s+me\s+back|go\s+back|back\s+please|previous\s+page)\b/i,
    ],
  },
  {
    id: "repeat_that",
    label: "Repeat",
    kind: "live",
    action: "repeat_last",
    patterns: [
      /^(?:repeat\s+that|say\s+that\s+again|repeat\s+please|come\s+again)\b/i,
    ],
  },
  // ── Registered-but-stubbed (architecture ready) ───────────────────────
  {
    id: "show_todays_cases",
    label: "Today's cases",
    kind: "stub",
    // Reserved: will filter Recent Cases by today's date on the mobile view.
    action: "stub_show_todays_cases",
    patterns: [
      /^show\s+today'?s?\s+cases\b/i,
      /^(?:what\s+are\s+)?today'?s?\s+cases\b/i,
    ],
  },
  {
    id: "search_for_case",
    label: "Search cases",
    kind: "stub",
    // Reserved: will capture the trailing token as a search query.
    action: "stub_search_for_case",
    patterns: [
      /^search\s+(?:for\s+)?cases?\b/i,
      /^find\s+case\s+/i,
    ],
  },
  {
    id: "summarize_this_case",
    label: "Summarize this case",
    kind: "stub",
    action: "stub_summarize_this_case",
    patterns: [
      /^summari[sz]e\s+(?:this|the\s+current)\s+case\b/i,
      /^give\s+me\s+a\s+case\s+summary\b/i,
    ],
  },
];

/**
 * Try to classify a raw transcript as a voice intent. Returns the matched
 * intent (with kind: "live" | "stub") or null.
 */
export function matchVoiceIntent(text) {
  if (!text || typeof text !== "string") return null;
  const t = text.trim();
  if (!t) return null;
  for (const intent of VOICE_INTENTS) {
    for (const p of intent.patterns) {
      if (p.test(t)) return intent;
    }
  }
  return null;
}
